import { NzTreeNode } from 'src/app/modules/shared/models/tree-node.model';

export function normalizeWbsSelectionRows(rows: string[][]): string[][] {
  return [...rows.filter((row) => row.length > 0), []];
}

export function updateWbsSelectionRows(
  rows: string[][],
  index: number,
  value: string[] | null,
): string[][] {
  const next = rows.map((row) => [...row]);

  if (!value || value.length === 0) {
    next.splice(index, 1);
    return normalizeWbsSelectionRows(next);
  }

  next[index] = value;
  if (isAllSelection(value)) {
    const parentPath = normalizePath(value);
    return normalizeWbsSelectionRows(
      next.filter(
        (row, rowIndex) =>
          rowIndex === index ||
          (!isSamePath(parentPath, normalizePath(row)) &&
            !isPathPrefix(parentPath, normalizePath(row))),
      ),
    );
  }

  return normalizeWbsSelectionRows(next);
}

export function getWbsSectionItemIds(rows: string[][]): string[] {
  return Array.from(
    new Set(
      rows
        .filter((row) => row.length > 0)
        .map((row) => row[row.length - 1].split('#')[1]),
    ),
  );
}

export function getWbsSectionOptions(
  nodes: NzTreeNode[],
  rows: string[][],
  currentIndex: number,
): NzTreeNode[] {
  const selectedRows = rows.filter(
    (row, index) => index !== currentIndex && row.length > 0,
  );

  return nodes.map((node) =>
    cloneNodeWithDisabledState(node, [], selectedRows),
  );
}

function cloneNodeWithDisabledState(
  node: NzTreeNode,
  parentPath: string[],
  selectedRows: string[][],
): NzTreeNode {
  const path = [...parentPath, node.value.toString()];
  const normalizedPath = normalizePath(path);
  const disabled = selectedRows.some((selectedRow) => {
    const selectedPath = normalizePath(selectedRow);
    if (isAllSelection(selectedRow)) {
      return (
        isSamePath(selectedPath, normalizedPath) ||
        isPathPrefix(selectedPath, normalizedPath)
      );
    }
    return !!node.isLeaf && isSamePath(selectedPath, normalizedPath);
  });
  const children = (node.children as NzTreeNode[] | undefined)?.map((child) =>
    cloneNodeWithDisabledState(child, path, selectedRows),
  );

  return {
    ...node,
    disabled,
    children,
  };
}

function isAllSelection(path: string[]): boolean {
  return path.length > 1 && path[path.length - 1] === path[path.length - 2];
}

function normalizePath(path: string[]): string[] {
  return isAllSelection(path) ? path.slice(0, -1) : path;
}

function isSamePath(first: string[], second: string[]): boolean {
  return (
    first.length === second.length &&
    first.every((value, index) => value === second[index])
  );
}

function isPathPrefix(parent: string[], child: string[]): boolean {
  return (
    parent.length < child.length &&
    parent.every((value, index) => value === child[index])
  );
}
